import { ChangeDetectionStrategy, ChangeDetectorRef, Component, HostBinding, Inject, Input, OnInit } from '@angular/core';
import { IMODALS_SERVICE, IModalsService, P500UIBaseModal } from '@platform500services/p500-ui-kit';
import { HttpClient } from '@angular/common/http';
import { IEnvironmentService } from '../../services/environment/environment.interface';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { IDataService, IParsedDataResponse } from '../../services/data/data-service.interface';
import { forkJoin } from 'rxjs';
import { IMetricsService } from '../../../modules/settings/crm-metrics/services/metrics-service.interface';
import { DestroySubscribers } from '../../decorators/destroy-subscribers.decorator';
import { PageServiceInterface } from '../../services/page/page-service.interface';
import { NetworkMetricRequest } from './interfaces/network-metric-request.interface';
import { CalculatorHelpers } from '../../components/calculator/helpers/calculator-helpers';
import { Router } from '@angular/router';
import { formatLocalization } from '../../components/base-metric-card/helpers/base-metrics.helpers';

@Component({
  selector: 'gtd-network-terms-modal',
  templateUrl: './network-terms.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
@DestroySubscribers()
export class NetworkTermsComponent extends P500UIBaseModal implements OnInit {
  @Input() id: number;
  @HostBinding('class') get className() {
    return `c-modal c-modal--d c-modal--action-form -primary ${this.isLoading ? 'c-modal--is--isLoading' : ''}`;
  }

  private api: string;
  public isLoading = true;
  public formulaInvalid: boolean;
  public globalData: IParsedDataResponse;
  public title: string;
  public description: string;
  public formats = {};
  public form = new UntypedFormGroup({
    name: new UntypedFormControl(null, Validators.required),
    format: new UntypedFormControl(null, Validators.required),
    description: new UntypedFormControl(),
  });
  public metrics: { id: number; name: string }[];
  public formula: string;
  public initialFormula: string;
  public calculatorHelpers = CalculatorHelpers;
  public inputFocused = false;

  subscribers: any = {};

  constructor(
    @Inject(IMODALS_SERVICE) public modalsService: IModalsService,
    @Inject('IEnvironmentService') private envService: IEnvironmentService,
    @Inject('IDataService') private dataService: IDataService,
    @Inject('IMetricsService') private metricsService: IMetricsService,
    @Inject('PageServiceInterface') private pageService: PageServiceInterface,
    private http: HttpClient,
    public cd: ChangeDetectorRef,
    public router: Router
  ) {
    super(modalsService, router);
    this.api = (this.envService.getVar('services.api') as string).split('/api')[0];
    this.globalData = this.dataService.getData();
  }

  ngOnInit(): void {
    this.isLoading = true;
    const metrics = this.metricsService.getBaseMetrics();
    const settings = this.metricsService.getMetricsSettings();
    this.subscribers.metricsData = forkJoin({ settings, metrics }).subscribe((data: any) => {
      this.metrics = data.metrics;
      data.settings.formats[0].forEach((f) => (this.formats[f.namespace] = formatLocalization.get(f.label) ?? f.label));

      if (this.id) {
        this.title = $localize`:@@settings.edit_network_metric:Edit Network Metric`;
        this.description = $localize`:@@settings.edit_network_metric_description:Edit of Network Metric`;
        this.subscribers.findMetric?.unsubscribe();
        this.subscribers.findMetric = this.http
          .post(`${this.api}/api/metrics/find`, {
            id: this.id,
          })
          .subscribe((metricData: any) => {
            this.initialFormula = metricData.formula;
            this.formulaChange(metricData.formula);
            this.nameControl.setValue(metricData.name);
            this.formatControl.setValue(metricData.format);
            this.descriptionControl.setValue(metricData.description);
            this.isLoading = false;
            this.cd.markForCheck();
          });
      } else {
        this.title = $localize`:@@settings.create_network_metric:Create Network Metric`;
        this.description = $localize`:@@settings.create_network_metric_description:Creation of Network Metric`;
        this.isLoading = false;
        this.cd.markForCheck();
      }
    });
  }

  submit(): void {
    const body: NetworkMetricRequest = {
      metric_type: 'App\\Modules\\Metrics\\MetricTypes\\NetworkMetric',
      formula: this.formula,
      name: this.nameControl.value,
      format: this.formatControl.value,
      description: this.descriptionControl.value,
    };

    if (this.id) {
      body.id = this.id;
    }

    this.http.post(`${this.api}/api/metrics/${this.id ? 'edit' : 'create'}`, body).subscribe(() => {
      this.pageService.refresh();
      this.close();
    });
  }

  public formulaChange(newFormula: string): void {
    this.formula = newFormula;
    const dummyFormula = newFormula.replace(/{([^{}]+)}/g, (match) => {
      return this.calculatorHelpers.getMetricId(match);
    });
    try {
      const result = window.eval(dummyFormula);
      this.formulaInvalid = isNaN(result) || !isFinite(result);
    } catch {
      this.formulaInvalid = true;
    }
  }

  get nameControl(): UntypedFormControl {
    return this.form.get('name') as UntypedFormControl;
  }

  get formatControl(): UntypedFormControl {
    return this.form.get('format') as UntypedFormControl;
  }

  get descriptionControl(): UntypedFormControl {
    return this.form.get('description') as UntypedFormControl;
  }
}
